/**
 * Los dos pasos del 2FA OBLIGATORIO en el login de municipio. Se usan desde
 * pages/Login.jsx, que los muestra en vez del formulario de email/contraseña
 * cuando el backend no entrega todavía un JWT real:
 *
 *  - <Login2faCodigo>: el usuario ya tiene 2FA activo (`requiere_2fa`) y falta
 *    el código de 6 dígitos. → POST /api/auth/2fa/verificar
 *  - <Login2faConfigurar>: el usuario todavía NO tiene 2FA (`requiere_configurar_2fa`)
 *    y tiene que configurarlo antes de entrar: QR + clave + código. Es parte del
 *    login, no una pantalla aparte. → POST /api/auth/2fa/generar y /2fa/activar
 *
 * Los dos trabajan con el token temporal que devolvió el login (`token_2fa`),
 * y cuando el backend entrega el JWT real llaman a onCompleto(data, recordar):
 * guardarlo y redirigir es asunto de Login.jsx.
 *
 * Ambos ofrecen "Recordar este dispositivo por 30 días", tildado por defecto.
 * Distinguen "código mal tipeado, reintentá" de "el token temporal ya no sirve,
 * volvé a empezar" por el `codigo` del error del backend (CODIGO_INCORRECTO /
 * 2FA_VENCIDO), no por el texto del mensaje.
 *
 * Mismo estilo visual que Superadmin.jsx / SuperadminSeguridad.jsx (login.css).
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { STROKE_DEGRADE, IconCandado } from './icons';
import '../styles/login.css';

// Una demo vencida o revocada (invitados de tumunicipio) no tiene nada que reintentar en este paso:
// el backend responde 403 con alguno de estos códigos y hay que volver al login, donde se lee el motivo.
const CODIGOS_DEMO_SIN_ACCESO = ['DEMO_VENCIDA', 'DEMO_REVOCADA'];

const API_URL = process.env.REACT_APP_API_URL;

// Vida (en segundos) del token temporal, leída de su propio payload (exp - iat)
// para no depender de que la hora de la PC coincida con la del servidor. No
// verifica nada: la validez real la decide siempre el backend.
const segundosDeVida = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp - payload.iat;
  } catch {
    return 120;
  }
};

const minutosTexto = (segundos) => {
  const m = Math.round(segundos / 60);
  return m === 1 ? '1 minuto' : `${m} minutos`;
};

// La clave TOTP viene de corrido (32 caracteres); agrupada de a 4 se lee y se
// tipea mucho mejor. Las apps ignoran los espacios.
const agruparClave = (clave) => (clave || '').replace(/(.{4})/g, '$1 ').trim();

// Cuando se agota el tiempo del token temporal se avisa y se vuelve a empezar.
function useVencimiento(token2fa, onVolver) {
  useEffect(() => {
    const segundos = segundosDeVida(token2fa);
    const timer = setTimeout(() => {
      onVolver(`Se agotó el tiempo para completar este paso (${minutosTexto(segundos)}). Volvé a ingresar tu email y contraseña.`);
    }, segundos * 1000);
    return () => clearTimeout(timer);
    // onVolver llega como función nueva en cada render de Login.jsx: solo
    // importa reiniciar el reloj cuando cambia el token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token2fa]);
}

const CampoCodigo = ({ id, valor, onCambio, deshabilitado }) => (
  <div className="form-group">
    <label htmlFor={id}>Código de 6 dígitos</label>
    <div className="input-wrapper">
      <span className="input-icon"><IconCandado size={16} stroke={STROKE_DEGRADE} /></span>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        placeholder="000000"
        value={valor}
        onChange={(e) => onCambio(e.target.value.replace(/\D/g, '').slice(0, 6))}
        className="form-input"
        style={estilos.inputCodigo}
        readOnly={deshabilitado}
        autoFocus
      />
    </div>
  </div>
);

const CasillaRecordar = ({ id, marcada, onCambio, deshabilitado }) => (
  <div className="form-remember">
    <input
      id={id}
      type="checkbox"
      checked={marcada}
      onChange={(e) => onCambio(e.target.checked)}
      disabled={deshabilitado}
    />
    <label htmlFor={id}>Recordar este dispositivo por 30 días</label>
  </div>
);

const BotonVolver = ({ onClick, deshabilitado }) => (
  <button type="button" className="link-ayuda" onClick={onClick} disabled={deshabilitado} style={estilos.linkBoton}>
    Volver
  </button>
);

// ---------------------------------------------------------------------------
// Ya configurado: pide el código.
// ---------------------------------------------------------------------------
export const Login2faCodigo = ({ token2fa, onCompleto, onVolver }) => {
  const [codigo, setCodigo] = useState('');
  const [recordar, setRecordar] = useState(true);
  const [verificando, setVerificando] = useState(false);
  const [error, setError] = useState('');

  useVencimiento(token2fa, onVolver);

  const enviar = async (e) => {
    e.preventDefault();
    if (codigo.length !== 6) return;
    setError('');
    setVerificando(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/2fa/verificar`, {
        token_2fa: token2fa,
        codigo,
        recordar_dispositivo: recordar,
      });
      onCompleto(data, recordar);
    } catch (err) {
      console.error('Error al verificar el código de 2FA:', err);
      const status = err.response?.status;
      const codigoError = err.response?.data?.codigo;
      const mensaje = err.response?.data?.mensaje;
      if (codigoError === 'CODIGO_INCORRECTO') {
        // Se equivocó de código: se puede reintentar con el mismo token
        // temporal (mientras no venza).
        setError('El código no es correcto. Fijate el código actual de tu app (cambia cada 30 segundos) y probá de nuevo.');
        setCodigo('');
      } else if (status === 401 || CODIGOS_DEMO_SIN_ACCESO.includes(codigoError)) {
        // El token temporal venció o dejó de servir (o la demo venció): hay que empezar de nuevo.
        onVolver(mensaje || 'La verificación venció. Volvé a ingresar tu email y contraseña.');
      } else {
        // 429 (demasiados intentos), 400, error de red, etc.
        setError(mensaje || 'No se pudo verificar el código. Probá de nuevo.');
      }
    } finally {
      setVerificando(false);
    }
  };

  return (
    <>
      <h1 className="login-title">Verificación en dos pasos</h1>
      <p className="login-subtitle">Abrí tu app autenticadora e ingresá el código de 6 dígitos.</p>

      {/* key propia: sin ella React reutiliza los inputs del login (misma
          estructura) y el autoFocus del campo de código nunca se dispara. */}
      <form key="form-2fa-codigo" onSubmit={enviar} className="login-form">
        <CampoCodigo id="codigo2fa" valor={codigo} onCambio={setCodigo} deshabilitado={verificando} />
        <span style={estilos.textoAyuda}>Tenés {minutosTexto(segundosDeVida(token2fa))} para completar este paso.</span>

        <CasillaRecordar id="recordar-dispositivo" marcada={recordar} onCambio={setRecordar} deshabilitado={verificando} />

        {error && <div className="error-message">{error}</div>}

        <button type="submit" className="btn-ingresar" disabled={verificando || codigo.length !== 6}>
          {verificando ? 'Verificando...' : 'Verificar'}
        </button>
        <BotonVolver onClick={() => onVolver('')} deshabilitado={verificando} />
      </form>
    </>
  );
};

// ---------------------------------------------------------------------------
// Todavía sin 2FA: configuración obligatoria (QR + clave + código).
// ---------------------------------------------------------------------------
export const Login2faConfigurar = ({ token2fa, onCompleto, onVolver }) => {
  const [config, setConfig] = useState(null); // { secret, qr_data_url }
  const [cargandoQr, setCargandoQr] = useState(true);
  const [codigo, setCodigo] = useState('');
  const [recordar, setRecordar] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  useVencimiento(token2fa, onVolver);

  // Al entrar a este paso se pide el QR (el backend guarda el secret SIN
  // activar hasta que se confirme con un código real).
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API_URL}/auth/2fa/generar`, { token_2fa: token2fa });
        if (!cancelado) setConfig({ secret: data.secret, qr_data_url: data.qr_data_url });
      } catch (err) {
        if (cancelado) return;
        console.error('Error al generar la clave de 2FA:', err);
        if (err.response?.status === 401 || err.response?.status === 409
            || CODIGOS_DEMO_SIN_ACCESO.includes(err.response?.data?.codigo)) {
          onVolver(err.response?.data?.mensaje || 'La verificación venció. Volvé a ingresar tu email y contraseña.');
          return;
        }
        setError(err.response?.data?.mensaje || 'No se pudo generar el código QR. Volvé a intentar.');
      } finally {
        if (!cancelado) setCargandoQr(false);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token2fa]);

  const enviar = async (e) => {
    e.preventDefault();
    if (codigo.length !== 6 || !config) return;
    setError('');
    setEnviando(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/2fa/activar`, {
        token_2fa: token2fa,
        codigo,
        recordar_dispositivo: recordar,
      });
      // Quien acaba de activar el 2FA ya demostró que tiene el teléfono: el
      // backend entrega directo el JWT real, sin pedir un segundo código.
      onCompleto(data, recordar);
    } catch (err) {
      console.error('Error al activar el 2FA:', err);
      const status = err.response?.status;
      const codigoError = err.response?.data?.codigo;
      const mensaje = err.response?.data?.mensaje;
      if (codigoError === 'CODIGO_INCORRECTO') {
        setError('El código no es correcto. Fijate el código actual de tu app (cambia cada 30 segundos) y que la hora de tu teléfono esté en automático.');
        setCodigo('');
      } else if (status === 401 || status === 409 || CODIGOS_DEMO_SIN_ACCESO.includes(codigoError)) {
        onVolver(mensaje || 'La verificación venció. Volvé a ingresar tu email y contraseña.');
      } else {
        setError(mensaje || 'No se pudo activar la verificación. Probá de nuevo.');
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <h1 className="login-title">Activá la verificación en dos pasos</h1>
      <p className="login-subtitle">
        Por seguridad es obligatoria para ingresar. Te lleva un minuto y se hace una sola vez.
      </p>

      {cargandoQr && <p style={estilos.textoCentrado}>Preparando tu código QR...</p>}

      {!cargandoQr && !config && (
        <div className="login-form">
          {error && <div className="error-message">{error}</div>}
          <BotonVolver onClick={() => onVolver('')} deshabilitado={false} />
        </div>
      )}

      {config && (
        <div style={estilos.contenedor}>
          <div>
            <p style={estilos.subtituloSeccion}>1. Escaneá el código QR</p>
            <span style={estilos.textoAyuda}>
              Abrí Google Authenticator (o Authy, Microsoft Authenticator…), tocá “Agregar cuenta” y escaneá este código.
              Si no tenés ninguna, instalá una desde la tienda de aplicaciones de tu teléfono.
            </span>
            <div style={estilos.marcoQr}>
              <img
                src={config.qr_data_url}
                alt="Código QR para configurar la verificación en dos pasos"
                width={200}
                height={200}
                style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
              />
            </div>
          </div>

          <div>
            <p style={estilos.subtituloSeccion}>¿No podés escanearlo?</p>
            <span style={estilos.textoAyuda}>Cargá esta clave a mano en tu app:</span>
            <code style={estilos.clave}>{agruparClave(config.secret)}</code>
          </div>

          <form key="form-2fa-configurar" onSubmit={enviar} className="login-form">
            <p style={{ ...estilos.subtituloSeccion, margin: 0 }}>2. Confirmá con un código</p>
            <CampoCodigo id="codigo2fa-activar" valor={codigo} onCambio={setCodigo} deshabilitado={enviando} />
            <span style={estilos.textoAyuda}>El que muestra la app ahora mismo (cambia cada 30 segundos).</span>

            <CasillaRecordar id="recordar-dispositivo-activar" marcada={recordar} onCambio={setRecordar} deshabilitado={enviando} />

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="btn-ingresar" disabled={enviando || codigo.length !== 6}>
              {enviando ? 'Activando...' : 'Activar e ingresar'}
            </button>
            <BotonVolver onClick={() => onVolver('')} deshabilitado={enviando} />
          </form>
        </div>
      )}
    </>
  );
};

// --- estilos puntuales de estos pasos (el resto sale de login.css) ---
const estilos = {
  contenedor: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  subtituloSeccion: {
    margin: '0 0 6px',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: '#7f8c8d',
    fontFamily: 'var(--font-header)',
  },
  textoAyuda: {
    fontSize: 12,
    color: '#7f8c8d',
    lineHeight: 1.4,
  },
  textoCentrado: {
    textAlign: 'center',
    fontSize: 13,
    color: '#7f8c8d',
    margin: '24px 0',
  },
  linkBoton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    font: 'inherit',
    fontSize: 13,
    padding: 4,
  },
  marcoQr: {
    display: 'flex',
    justifyContent: 'center',
    margin: '12px 0 0',
    padding: 12,
    background: '#ffffff',
    border: '2px solid #e0e0e0',
    borderRadius: 12,
  },
  clave: {
    display: 'block',
    marginTop: 8,
    padding: '12px 14px',
    background: '#f8f9fa',
    border: '2px solid #e0e0e0',
    borderRadius: 12,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 14,
    letterSpacing: '1px',
    lineHeight: 1.6,
    color: '#2c3e50',
    textAlign: 'center',
    overflowWrap: 'anywhere',
    userSelect: 'all',
  },
  // El input de código va centrado y con letras espaciadas para que los 6
  // dígitos se lean de un vistazo (el padding izquierdo deja lugar al ícono).
  inputCodigo: {
    padding: '14px 14px 14px 40px',
    textAlign: 'center',
    letterSpacing: '8px',
    fontSize: 20,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
};
